import type { BranchSeat, Campaign, PlaylistSummary, SignageMedia, SignageScreen } from './api'

// The Signage tabs' list logic: finding, sorting, refusing and where a file is used. Pure, for vitest.

export type KindFilter = 'all' | 'image' | 'video'
export type MediaSort = 'newest' | 'oldest' | 'name' | 'size' | 'length'

export const MEDIA_SORTS: readonly MediaSort[] = ['newest', 'oldest', 'name', 'size', 'length']

/** Library picture and film limits, as internal/signage refuses them. */
export const MAX_PICTURE_MB = 10
export const MAX_FILM_MB = 40

/** The library files a search and a kind leave, in the chosen order. */
export function viewMedia(media: readonly SignageMedia[], find: string, kind: KindFilter, sort: MediaSort): SignageMedia[] {
  const q = find.trim().toLowerCase()
  const kept = media.filter((m) => (kind === 'all' || m.kind === kind) && (!q || m.name.toLowerCase().includes(q)))
  const by: Record<MediaSort, (a: SignageMedia, b: SignageMedia) => number> = {
    newest: (a, b) => b.createdAt.localeCompare(a.createdAt),
    oldest: (a, b) => a.createdAt.localeCompare(b.createdAt),
    name: (a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base', numeric: true }),
    size: (a, b) => b.bytes - a.bytes,
    length: (a, b) => (b.durationMs ?? 0) - (a.durationMs ?? 0),
  }
  return kept.sort((a, b) => by[sort](a, b) || a.id.localeCompare(b.id))
}

export type UploadRefusal = 'type' | 'pictureSize' | 'filmSize'

/** Why a file would be refused before it is sent: its kind or its size; undefined when it may go. */
export function uploadRefusal(file: { type: string; size: number; name: string }): UploadRefusal | undefined {
  const ext = file.name.toLowerCase().split('.').pop() ?? ''
  const picture = /^image\/(png|jpeg)$/.test(file.type) || (!file.type && ['png', 'jpg', 'jpeg'].includes(ext))
  const film = /^video\/(mp4|webm|quicktime)$/.test(file.type) || (!file.type && ['mp4', 'webm', 'mov'].includes(ext))
  if (!picture && !film) return 'type'
  if (picture && file.size > MAX_PICTURE_MB << 20) return 'pictureSize'
  if (film && file.size > MAX_FILM_MB << 20) return 'filmSize'
  return undefined
}

export interface Usage {
  playlists: Array<{ id: string; name: string }>
  campaigns: Array<{ id: string; name: string }>
  designs: string[]
}

/** Where each library file plays: the playlists holding it, their campaigns and their designs. */
export function usageByMedia(playlists: readonly PlaylistSummary[], campaigns: readonly Campaign[]): Map<string, Usage> {
  const out = new Map<string, Usage>()
  for (const p of playlists) {
    const ids = new Set(p.entries.map((e) => e.mediaItemId))
    const cs = campaigns.filter((c) => c.playlistId === p.id).map((c) => ({ id: c.id, name: c.name }))
    for (const id of ids) {
      const u = out.get(id) ?? { playlists: [], campaigns: [], designs: [] }
      u.playlists.push({ id: p.id, name: p.name })
      for (const c of cs) if (!u.campaigns.some((x) => x.id === c.id)) u.campaigns.push(c)
      for (const d of p.designs) if (!u.designs.includes(d)) u.designs.push(d)
      out.set(id, u)
    }
  }
  return out
}

/** Up to four distinct files of a playlist, in playing order, for its card's mosaic. */
export function mosaicOf(p: Pick<PlaylistSummary, 'entries'>, library: readonly SignageMedia[]): SignageMedia[] {
  const byId = new Map(library.map((m) => [m.id, m]))
  const out: SignageMedia[] = []
  for (const e of p.entries) {
    const m = byId.get(e.mediaItemId)
    if (m && !out.includes(m)) out.push(m)
    if (out.length === 4) break
  }
  return out
}

/** The ticked files after a tick: one toggled, or (shift) the run from the last tick to this one added. */
export function nextSelection(order: readonly string[], chosen: ReadonlySet<string>, id: string, range?: { anchor?: string }): Set<string> {
  if (range?.anchor && range.anchor !== id && order.includes(range.anchor) && order.includes(id)) {
    const i = order.indexOf(range.anchor)
    const j = order.indexOf(id)
    return new Set([...chosen, ...order.slice(Math.min(i, j), Math.max(i, j) + 1)])
  }
  const next = new Set(chosen)
  if (next.has(id)) next.delete(id)
  else next.add(id)
  return next
}

export type Ago = { key: 'signage.ago.now' | 'signage.ago.min' | 'signage.ago.hour' | 'signage.ago.day'; n: number }

/** A past moment as "just now", minutes, hours (under two days) or days ago. */
export function agoOf(at: string | undefined, now: number): Ago | null {
  if (!at) return null
  const t = Date.parse(at)
  if (Number.isNaN(t)) return null
  const min = Math.max(0, Math.floor((now - t) / 60_000))
  if (min < 1) return { key: 'signage.ago.now', n: 0 }
  if (min < 60) return { key: 'signage.ago.min', n: min }
  if (min < 48 * 60) return { key: 'signage.ago.hour', n: Math.floor(min / 60) }
  return { key: 'signage.ago.day', n: Math.floor(min / 1440) }
}

/** Bytes as KB, MB (one decimal) or GB (one decimal, whole when round). */
export function sizeOf(bytes: number): string {
  if (bytes < 1 << 20) return `${Math.max(1, Math.round(bytes / 1024))} KB`
  if (bytes < 1 << 30) return `${(bytes / (1 << 20)).toFixed(1)} MB`
  const gb = bytes / (1 << 30)
  return `${Number.isInteger(gb) ? gb : gb.toFixed(1)} GB`
}

/** Playlists by a search on their name, most recently edited first. */
export function viewPlaylists<P extends Pick<PlaylistSummary, 'name' | 'updatedAt'>>(lists: readonly P[], find: string): P[] {
  const q = find.trim().toLowerCase()
  return lists.filter((p) => !q || p.name.toLowerCase().includes(q)).sort((a, b) => b.updatedAt.localeCompare(a.updatedAt))
}

export type BranchProblem = { key: 'signage.state.noSeat' | 'signage.noScreen' | 'signage.noArea'; designId?: string }

/** Why a campaign cannot show at a branch, as precisely as the screens say: no seat, no main display, or its design has no playlist area. */
export function branchProblem(b: Pick<BranchSeat, 'id' | 'seat' | 'playlistArea'>, screens: readonly SignageScreen[] | null): BranchProblem | undefined {
  if (!b.seat) return { key: 'signage.state.noSeat' }
  if (b.playlistArea) return undefined
  const here = screens?.filter((s) => s.branchId === b.id) ?? []
  if (screens && here.length === 0) return { key: 'signage.noScreen' }
  const design = here.find((s) => s.design && !s.playlistArea)?.design
  return design ? { key: 'signage.noArea', designId: design.id } : { key: 'signage.noArea' }
}
